import React, { useEffect, useState } from 'react';
import Slider from '@mui/material/Slider';
import cn from 'classnames';

import { IRangeSliderProps, TRangeSliderValue } from './RangeSlider.types';

import { validationErrMessages } from '@constants/validationConstants';

import GridContainer from '../GridContainer';
import Input from '../Input';

import './RangeSlider.scss';

const RangeSlider = ({
	className,
	disabled,
	fractionDigits,
	fromLabel = 'From',
	fromName,
	fromPlaceholder = 'From',
	handleChange = () => null,
	label,
	max,
	min,
	register,
	setValue,
	step,
	toLabel = 'To',
	toName,
	toPlaceholder = 'To',
	value,
	valueLabelDisplay = 'auto',
	withInputs = true,
	withUseForms = false,
	valueLabelPrecision = 2,
}: IRangeSliderProps) => {
	const [error, setError] = useState({
		from: '',
		to: '',
	});

	const handleChangeSlider = (_e: any, newValue: TRangeSliderValue) => {
		setValue(newValue as Array<number>);

		handleChange(newValue);
	};

	const handleChangeFromInput = (newValue: string) => {
		if (isNaN(+newValue) || (
			fractionDigits
			&& newValue.split('.')[1]
			&& String(newValue.split('.')[1]).length > fractionDigits)
		) return;

		if (setValue && value) {
			if (newValue[newValue.length - 1] === '.' && fractionDigits) {
				const preparedValue = [newValue, (value as any)[1] as number];

				setValue(preparedValue);
			} else {
				const preparedValue = [+newValue, (value as any)[1] as number];

				setValue(preparedValue);
				handleChange(preparedValue);
			}
		}
	};

	const handleChangeToInput = (newValue: string) => {
		if (isNaN(+newValue) || (
			fractionDigits
			&& newValue.split('.')[1]
			&& String(newValue.split('.')[1]).length > fractionDigits)
		) return;

		if (newValue[newValue.length - 1] === '.' && fractionDigits) {
			const preparedValue = [(value as any)[0] as number, newValue];

			setValue(preparedValue);
		} else {
			const preparedValue = [(value as any)[0] as number, +newValue];

			setValue(preparedValue);
			handleChange(preparedValue);
		}
	};

	useEffect(() => {
		if (!value.length || min === undefined || max === undefined) return;

		const isFromValueValid = Number(value[0]) >= Number(min) && Number(value[0]) <= Number(max);
		const isToValueValid = Number(value[1]) >= Number(min) && Number(value[1]) <= Number(max);

		setError({
			from: isFromValueValid ? '' : validationErrMessages.assetHeight,
			to: isToValueValid ? '' : validationErrMessages.assetHeight,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	return (
		<div className={cn('f5gRangeSliderComponentWrapper', className)}>
			{label && <div className='f5gRangeSliderLabel'>{label}</div>}
			<div className='range-slider'>
				<Slider
					className='range-slider-wrapper'
					disabled={disabled}
					marks={!!step}
					max={max}
					min={min}
					onChange={handleChangeSlider}
					step={step}
					value={[+value[0], +value[1]] as Array<number>}
					valueLabelDisplay={valueLabelDisplay}
					valueLabelFormat={(v: number) => `${+v.toFixed(valueLabelPrecision)}`}
				/>
				<div className='f5gRangeSliderMarkMin'>{min}</div>
				<div className='f5gRangeSliderMarkMax'>{max}</div>
			</div>
			{withInputs && (
				<GridContainer columns={2}>
					<Input
						disabled={disabled}
						errorMessage={error.from}
						label={fromLabel}
						name={fromName}
						onChange={handleChangeFromInput}
						placeholder={fromPlaceholder}
						register={register}
						value={value[0]}
						withUseForms={withUseForms}
					/>
					<Input
						disabled={disabled}
						errorMessage={error.to}
						label={toLabel}
						name={toName}
						onChange={handleChangeToInput}
						placeholder={toPlaceholder}
						register={register}
						value={value[1]}
						withUseForms={withUseForms}
					/>
				</GridContainer>
			)}
		</div>
	);
};

export default RangeSlider;
